import {
  Component,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { AlertCircle, Home, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DotPattern } from "@/components/ui/dot-pattern";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";
import { getErrorInfo, shouldIgnoreError } from "@/lib/errors";

type ErrorFallbackProps = {
  error: unknown;
  resetError: () => void;
  eventId?: string;
  componentStack?: string;
  onVisibilityChange: (visible: boolean) => void;
};

type ErrorMeta = ReturnType<typeof getErrorInfo>;

type ButtonRef = MutableRefObject<HTMLButtonElement | null>;

type FancyFallbackContentProps = {
  info: ErrorMeta;
  componentStack?: string;
  eventId?: string;
  onRetry: () => void;
  onGoHome: () => void;
  onToggleDetails: () => void;
  showDetails: boolean;
  retryButtonRef: ButtonRef;
};

type MinimalFallbackContentProps = {
  info: ErrorMeta;
  componentStack?: string;
  eventId?: string;
  onRetry: () => void;
  onGoHome: () => void;
  onToggleDetails: () => void;
  showDetails: boolean;
  retryButtonRef: ButtonRef;
};

class FallbackGuard extends Component<
  {
    children: ReactNode;
    onError?: (error: unknown) => void;
  },
  { hasError: boolean }
> {
  public state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

function FancyFallbackContent({
  info,
  componentStack,
  eventId,
  onRetry,
  onGoHome,
  onToggleDetails,
  showDetails,
  retryButtonRef,
}: FancyFallbackContentProps) {
  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-background via-background/95 to-background/80 px-4 py-12"
      role="alert"
      aria-live="assertive"
    >
      <DotPattern
        width={20}
        height={20}
        className="text-muted-foreground/15"
        glow
      />

      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <Card
        className={cn(
          "relative z-10 w-full max-w-lg overflow-hidden border-border/50 bg-background/85 backdrop-blur",
          "shadow-lg shadow-black/10 supports-[backdrop-filter]:bg-background/70"
        )}
      >
        <CardHeader className="flex flex-col items-center gap-4 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-7 w-7" aria-hidden />
          </span>
          <CardTitle>Algo deu errado!</CardTitle>
          <CardDescription>
            Ocorreu um erro inesperado. Nossa equipe foi notificada e já está investigando.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button ref={retryButtonRef} onClick={onRetry} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
              Tentar novamente
            </Button>
            <Button variant="outline" onClick={onGoHome} className="flex-1">
              <Home className="mr-2 h-4 w-4" aria-hidden />
              Voltar para início
            </Button>
          </div>

          {eventId ? (
            <p className="text-sm text-muted-foreground">
              ID do relatório: <span className="font-medium text-foreground">{eventId}</span>
            </p>
          ) : null}

          <div className="space-y-2">
            <button
              type="button"
              onClick={onToggleDetails}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {showDetails ? "Ocultar detalhes técnicos" : "Mostrar detalhes técnicos"}
            </button>
            {showDetails ? (
              <div className="rounded-md border border-border/60 bg-muted/40 p-4 text-left text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{info.name ?? "Erro"}</p>
                <p className="mt-1 break-all text-xs">{info.message}</p>
                {componentStack ? (
                  <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed">
                    {componentStack}
                  </pre>
                ) : null}
                {info.stack ? (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-semibold text-primary">
                      Stack trace
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed">
                      {info.stack}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MinimalFallbackContent({
  info,
  componentStack,
  eventId,
  onRetry,
  onGoHome,
  onToggleDetails,
  showDetails,
  retryButtonRef,
}: MinimalFallbackContentProps) {
  const containerStyle: CSSProperties = {
    alignItems: "center",
    background: "#0f172a",
    color: "#f8fafc",
    display: "flex",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "2rem 1rem",
    textAlign: "center",
  };

  const panelStyle: CSSProperties = {
    background: "rgba(15, 23, 42, 0.92)",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    borderRadius: "0.75rem",
    maxWidth: "32rem",
    padding: "2rem",
    width: "100%",
  };

  const buttonStyle: CSSProperties = {
    background: "#2563eb",
    border: "none",
    borderRadius: "0.5rem",
    color: "#f8fafc",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 600,
    padding: "0.75rem 1rem",
    width: "100%",
  };

  const outlineButtonStyle: CSSProperties = {
    ...buttonStyle,
    background: "transparent",
    border: "1px solid rgba(148, 163, 184, 0.5)",
    color: "#e2e8f0",
  };

  return (
    <div style={containerStyle} role="alert" aria-live="assertive">
      <div style={panelStyle}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Algo deu errado</h2>
        <p style={{ marginTop: "0.5rem", fontSize: "0.95rem", color: "#cbd5f5" }}>
          Estamos enfrentando uma instabilidade temporária. Já fomos notificados e vamos investigar.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1.5rem" }}>
          <button ref={retryButtonRef} type="button" style={buttonStyle} onClick={onRetry}>
            Tentar novamente
          </button>
          <button type="button" style={outlineButtonStyle} onClick={onGoHome}>
            Voltar para início
          </button>
        </div>

        {eventId ? (
          <p style={{ marginTop: "1rem", fontSize: "0.85rem", color: "#cbd5f5" }}>
            Referência do incidente: <span style={{ color: "#f8fafc" }}>{eventId}</span>
          </p>
        ) : null}

        <div style={{ marginTop: "1.5rem", textAlign: "left" }}>
          <button
            type="button"
            onClick={onToggleDetails}
            style={{
              background: "transparent",
              border: "none",
              color: "#93c5fd",
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: 600,
              padding: 0,
              textDecoration: "underline",
            }}
          >
            {showDetails ? "Ocultar detalhes técnicos" : "Mostrar detalhes técnicos"}
          </button>
          {showDetails ? (
            <div
              style={{
                background: "rgba(15, 23, 42, 0.75)",
                border: "1px solid rgba(148, 163, 184, 0.3)",
                borderRadius: "0.5rem",
                marginTop: "0.75rem",
                maxHeight: "12rem",
                overflow: "auto",
                padding: "0.75rem",
                fontSize: "0.75rem",
              }}
            >
              <p style={{ fontWeight: 600 }}>{info.name ?? "Erro"}</p>
              <p style={{ marginTop: "0.25rem", wordBreak: "break-word" }}>{info.message}</p>
              {componentStack ? (
                <pre style={{ marginTop: "0.5rem", whiteSpace: "pre-wrap" }}>{componentStack}</pre>
              ) : null}
              {info.stack ? (
                <details style={{ marginTop: "0.5rem" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>Stack trace</summary>
                  <pre style={{ marginTop: "0.5rem", whiteSpace: "pre-wrap" }}>{info.stack}</pre>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ErrorFallback({
  error,
  resetError,
  eventId,
  componentStack,
  onVisibilityChange,
}: ErrorFallbackProps) {
  const navigate = useNavigate();
  const [usePlainFallback, setUsePlainFallback] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const fancyRetryButtonRef = useRef<HTMLButtonElement | null>(null);
  const plainRetryButtonRef = useRef<HTMLButtonElement | null>(null);

  const info = useMemo(() => getErrorInfo(error), [error]);

  const handleRetry = useCallback(() => {
    resetError();
  }, [resetError]);

  const handleGoHome = useCallback(() => {
    navigate({ to: "/" });
  }, [navigate]);

  const handleToggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  useEffect(() => {
    setUsePlainFallback(false);
  }, [error]);

  useEffect(() => {
    onVisibilityChange(true);
    return () => onVisibilityChange(false);
  }, [onVisibilityChange]);

  useEffect(() => {
    if (usePlainFallback) {
      plainRetryButtonRef.current?.focus();
      return;
    }
    fancyRetryButtonRef.current?.focus();
  }, [usePlainFallback]);

  const handleGuardError = useCallback((guardError: unknown) => {
    if (import.meta.env.DEV) {
      console.error("[Fallback guard] styled fallback failed, switching to plain UI", guardError);
    }
    setUsePlainFallback(true);
  }, []);

  if (usePlainFallback) {
    return (
      <MinimalFallbackContent
        info={info}
        componentStack={componentStack}
        eventId={eventId}
        onRetry={handleRetry}
        onGoHome={handleGoHome}
        onToggleDetails={handleToggleDetails}
        showDetails={showDetails}
        retryButtonRef={plainRetryButtonRef}
      />
    );
  }

  return (
    <FallbackGuard key={`${Date.now()}-${info.message ?? "fallback"}`} onError={handleGuardError}>
      <FancyFallbackContent
        info={info}
        componentStack={componentStack}
        eventId={eventId}
        onRetry={handleRetry}
        onGoHome={handleGoHome}
        onToggleDetails={handleToggleDetails}
        showDetails={showDetails}
        retryButtonRef={fancyRetryButtonRef}
      />
    </FallbackGuard>
  );
}

const SentryBoundary = Sentry.ErrorBoundary;

function AppErrorBoundary({ children }: { children: ReactNode }) {
  const locationKey = useRouterState({
    select: (state) =>
      state.location?.href ??
      `${state.location?.pathname ?? ""}${state.location?.search ?? ""}${state.location?.hash ?? ""}`,
  });
  const effectiveResetKey = locationKey ?? "__app__";

  const ignoreResetCountRef = useRef(0);
  const ignoreResetScheduledRef = useRef(false);

  const handleFallbackVisibilityChange = useCallback((visible: boolean) => {
    if (!visible) {
      ignoreResetCountRef.current = 0;
      ignoreResetScheduledRef.current = false;
    }
  }, []);

  const scheduleIgnoredErrorReset = useCallback((resetError: () => void) => {
    if (ignoreResetScheduledRef.current || ignoreResetCountRef.current >= 1) {
      return;
    }

    ignoreResetCountRef.current += 1;
    ignoreResetScheduledRef.current = true;

    const runReset = () => {
      ignoreResetScheduledRef.current = false;
      ignoreResetCountRef.current = 0;
      resetError();
    };

    if (typeof queueMicrotask === "function") {
      queueMicrotask(runReset);
    } else {
      Promise.resolve().then(runReset).catch((error) => {
        if (import.meta.env.DEV) {
          console.error("[Boundary] failed to schedule reset", error);
        }
      });
    }
  }, []);

  return (
    <SentryBoundary
      key={effectiveResetKey}
      fallback={({
        error,
        resetError,
        eventId,
        componentStack,
      }: {
        error: unknown;
        resetError: () => void;
        eventId?: string;
        componentStack?: string;
      }) => {
        if (shouldIgnoreError(error)) {
          if (import.meta.env.DEV) {
            console.debug("[Ignored Error]", error);
          }
          scheduleIgnoredErrorReset(resetError);
          handleFallbackVisibilityChange(false);
          return <span aria-hidden="true" />;
        }

        return (
          <ErrorFallback
            error={error}
            resetError={resetError}
            eventId={eventId}
            componentStack={componentStack}
            onVisibilityChange={handleFallbackVisibilityChange}
          />
        );
      }}
      beforeCapture={(scope: Sentry.Scope, error: unknown) => {
        if (shouldIgnoreError(error)) {
          scope.addEventProcessor(() => null);
          return;
        }

        scope.setTags({ section: "app", handled: true });
        if (typeof window !== "undefined") {
          scope.setContext("environment", {
            userAgent: window.navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            location: window.location.href,
          });
        }
      }}
      onError={(error: unknown, componentStack?: string) => {
        if (shouldIgnoreError(error)) {
          return;
        }
        if (import.meta.env.DEV) {
          console.error("[Boundary Error]", error, componentStack);
        }
      }}
    >
      {children}
    </SentryBoundary>
  );
}

export { AppErrorBoundary };
export default AppErrorBoundary;
